import type { SupabaseClient } from '@supabase/supabase-js';

export interface CalendarCoreItem {
    id: string;
    name: string;
    color: string;
    visible: boolean;
    is_default: boolean;
    user_id: string;
    created_at?: string;
}

function isMissingCalendarsTable(err: any) {
    const msg = String(err?.message || err?.details || err);
    return /relation\s+\"?(public\.)?calendars\"?\s+does not exist/i.test(msg) || /table .*calendars.* does not exist/i.test(msg);
}

export async function listCalendarsCore(sb: SupabaseClient, userId: string): Promise<CalendarCoreItem[]> {
    const { data, error } = await sb
        .from('calendars')
        .select('*')
        .eq('user_id', userId)
        .order('is_default', { ascending: false })
        .order('name');
    if (error) {
        if (isMissingCalendarsTable(error)) {
            throw new Error('Calendars feature not initialized. Apply migration 20250818_create_calendars.sql');
        }
        throw error;
    }
    return (data || []) as CalendarCoreItem[];
}

export async function createCalendarCore(sb: SupabaseClient, userId: string, input: { name: string; color: string; visible?: boolean; is_default?: boolean }): Promise<CalendarCoreItem> {
    const { data, error } = await sb
        .from('calendars')
        .insert({
            name: input.name,
            color: input.color,
            visible: input.visible ?? true,
            is_default: input.is_default ?? false,
            user_id: userId,
        })
        .select('*')
        .single();
    if (error) {
        if (isMissingCalendarsTable(error)) {
            throw new Error('Calendars feature not initialized. Apply migration 20250818_create_calendars.sql');
        }
        throw error;
    }
    return data as CalendarCoreItem;
}

export async function updateCalendarCore(sb: SupabaseClient, id: string, patch: Partial<Pick<CalendarCoreItem, 'name' | 'color' | 'visible'>>): Promise<CalendarCoreItem> {
    const { data, error } = await sb
        .from('calendars')
        .update({
            ...(patch.name !== undefined ? { name: patch.name } : {}),
            ...(patch.color !== undefined ? { color: patch.color } : {}),
            ...(patch.visible !== undefined ? { visible: patch.visible } : {}),
        })
        .eq('id', id)
        .select('*')
        .single();
    if (error) {
        if (isMissingCalendarsTable(error)) {
            throw new Error('Calendars feature not initialized. Apply migration 20250818_create_calendars.sql');
        }
        throw error;
    }
    return data as CalendarCoreItem;
}

export async function deleteCalendarCore(sb: SupabaseClient, id: string): Promise<void> {
    const { error } = await sb
        .from('calendars')
        .delete()
        .eq('id', id);
    if (error) {
        if (isMissingCalendarsTable(error)) {
            throw new Error('Calendars feature not initialized. Apply migration 20250818_create_calendars.sql');
        }
        throw error;
    }
}
